'use client';

import getImages from '@/actions/projects/getImages';
import { VideoPlayer } from '@/components/ui/video-player';
import { useWindowWidth } from '@/lib/useWindowWidth';
import { StrapiImage } from '@/types/img';
import { Project } from '@/types/project';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';

interface Props {
  project: Project;
  locale: string;
  slug: string;
}

export default function ImagesSection({ project, locale, slug }: Props) {
  const [images, setImages] = useState<
    {
      id: number;
      indexId: number;
      img: StrapiImage;
      media: 'image' | 'video';
    }[]
  >([]);

  const videoRef = useRef<HTMLVideoElement>(null);
  const [isMuted, setIsMuted] = useState(true);

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
    }
  };

  const rowHeight = 10;
  const getRowSpan = (h: number) => Math.ceil(h / rowHeight);

  useEffect(() => {
    getImages({
      locale: locale,
      slug,
      baseUrl: true,
    }).then((res) => {
      if (res.data?.data) {
        let imgArr = res.data.data[0]?.images;
        if (!!imgArr?.length) {
          const sortedArr = imgArr.sort((a, b) => a.indexId - b.indexId);
          setImages(sortedArr);
        }
      }
    });
  }, [project]);

  const width = useWindowWidth();

  const largeSizes = [
    { w: 1598, h: 761 },
    { w: 628, h: 628 },
    { w: 952, h: 748 },
    { w: 628, h: 628 },
    { w: 952, h: 500 },
  ];

  const middleSizes = [
    { w: 1330, h: 650 },
    { w: 550, h: 570 },
    { w: 765, h: 641 },
    { w: 550, h: 570 },
    { w: 765, h: 475 },
  ];

  const smallSizes = [
    { w: 1070, h: 510 },
    { w: 420, h: 420 },
    { w: 637, h: 501 },
    { w: 420, h: 420 },
    { w: 637, h: 335 },
  ];

  const sizes =
    width >= 1650 ? largeSizes : width >= 1400 ? middleSizes : smallSizes;

  return (
    <div>
      <div className='flex justify-center w-full h-full'>
        <section
          className={`${
            width <= 1650 ? 'w-[1070px]' : 'w-full '
          } sm:hidden masonry-gallery-middle`}
        >
          {images[0] && (
            <div
              className={`masonry-item-middle masonry-item-middle-first ${
                width <= 1650 ? 'h-[510px]' : 'h-[850px]'
              }`}
              style={{
                width: `${sizes[0]?.w}px`,
                height: `${sizes[0]?.h}px`,
              }}
            >
              {images[0]?.media === 'video' ? (
                <VideoPlayer
                  src={
                  typeof images[0].img?.url === 'string'
                    ? images[0].img.url
                    : (images[0].img?.url as { src?: string })?.src ?? ''
                }
                  // width={sizes[0]?.w}
                  // height={sizes[0]?.h}
                />
              ) : images[0]?.img?.url &&
                images[0]?.img?.width &&
                images[0]?.img?.height ? (
                <Image
                  src={images[0].img.url}
                  width={sizes[0]?.w}
                  height={sizes[0]?.h}
                  className='object-cover w-full h-full rounded-[20px]'
                  alt=''
                  unoptimized
                  onContextMenu={(e) => e.preventDefault()}
                />
              ) : null}
            </div>
          )}

          {images.slice(1).map((i, idx) => {
            const realIdx = idx + 1; // т.к. срезали первый
            const fallbackSize = { w: 600, h: 600 };
            const { w, h } = sizes[realIdx] || fallbackSize;

            const alignmentClass =
              realIdx === 1 || realIdx === 2 ? 'align-top' : 'align-bottom';

            return (
              <div
                className={`masonry-item-middle ${alignmentClass}`}
                key={realIdx}
                style={{ width: `${w}px`, height: `${h}px` }}
              >
                {i?.media === 'video' ? (
                  <VideoPlayer src={
                    typeof i.img?.url === 'string'
                      ? i.img.url
                      : (i.img?.url as { src?: string })?.src ?? ''
                  } />
                ) : i?.img?.url && i?.img?.width && i?.img?.height ? (
                  <Image
                    src={i.img.url}
                    width={w}
                    height={h}
                    className='object-cover w-full h-full rounded-[20px]'
                    alt=''
                    unoptimized
                    onContextMenu={(e) => e.preventDefault()}
                  />
                ) : null}
              </div>
            );
          })}
        </section>
      </div>
      {/* Mobile */}
      <section className='hidden sm:flex flex-col px-4 text-[#101011]'>
        <div className=' masonry-gallery-full-mobile'>
          {images.map((i, idx) => {
            const isFirstTwo = idx < 2;
            const height = isFirstTwo ? 470 : 249;

            return (
              <div
                key={i.id}
                className={`relative w-full h-[${height}px]  masonry-item-full-mobile mb-[14px]`}
              >
                {i.media === 'video' ? (
                  <VideoPlayer
                    src={
                      typeof i.img?.url === 'string'
                        ? i.img.url
                        : (i.img?.url as { src?: string })?.src ?? ''
                    }
                    // width={i.img.width}
                    // height={i.img.height}
                  />
                ) : i?.img?.url && i?.img?.width && i?.img?.height ? (
                  <Image
                    src={i.img.url}
                    alt=''
                    fill
                    className='object-cover rounded-[20px]'
                    unoptimized
                    onContextMenu={(e) => e.preventDefault()}
                  />
                ) : null}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
